import type { StytchJWTPayload } from '@/types/stytch'

/**
 * Detects an impersonated Client Portal session from the Stytch JWT.
 *
 * The long-term signal is Stytch's official `impersonated` authentication factor.
 * Trusted Auth Token (TAT) custom claims (`impersonator_auth_user_id` /
 * `impersonator_email`) are a transitional fallback until TAT impersonation is
 * configured (CP-1318). The TAT branch also requires a `trusted_auth_token`
 * authentication factor so stray JWT claims cannot false-positive. Prefer the
 * official factor in new call sites, and delete the TAT branch once that rollout
 * lands rather than keeping dual detection.
 */
export function isImpersonatingSession(payload: StytchJWTPayload | null | undefined): boolean {
  if (!payload) return false
  const factors = payload['https://stytch.com/session']?.authentication_factors ?? []
  if (factors.some((factor) => factor.type === 'impersonated')) return true

  const hasTrustedAuthTokenFactor = factors.some((factor) => factor.type === 'trusted_auth_token')
  if (!hasTrustedAuthTokenFactor) return false

  if (payload.impersonator_auth_user_id != null && payload.impersonator_auth_user_id !== '') {
    return true
  }
  return Boolean(payload.impersonator_email?.trim())
}
